import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, ExternalLink, Save, X } from "lucide-react";

const PERSONAS = [
  { id: "both", label: "Both" },
  { id: "retire", label: "Retire" },
  { id: "relocate", label: "Relocate" },
];

function PersonaPill({ persona }) {
  const color =
    persona === "retire"
      ? "bg-[#183129] text-white"
      : persona === "relocate"
        ? "bg-[#451C25] text-white"
        : "bg-[#451C25]/10 text-[#451C25]";
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase ${color}`}
    >
      {persona}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-transparent border border-[#451C25]/20 px-3 py-2 focus:outline-none focus:border-[#451C25] transition-colors text-sm"
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className="w-full bg-transparent border border-[#451C25]/20 px-3 py-2 focus:outline-none focus:border-[#451C25] transition-colors text-sm font-mono"
    />
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]"
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/* ============================================================
   SERVICES PANEL
============================================================ */
export function ServicesPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service id or "new"

  const fetchAll = () => {
    setLoading(true);
    axios
      .get("/admin/services")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load services"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, [axios]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`/admin/services/${id}`);
      toast.success("Service deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="services-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-[#451C25]">
            Services & Packages
          </h2>
          <p className="text-sm text-[#6B4954] mt-1">
            Manage what you sell on the home page. CTA links can go to Calendly,
            Stripe, or any URL.
          </p>
        </div>
        <button
          data-testid="service-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New Service
        </button>
      </div>

      {loading ? (
        <p className="text-[#6B4954]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#6B4954]">No services yet. Click "New Service".</p>
      ) : (
        <div className="border border-[#451C25]/10 bg-white divide-y divide-[#451C25]/10">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.slug}`}
              className="p-5 flex items-center gap-5"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-serif text-lg text-[#451C25]">{s.title}</h3>
                  {s.featured && (
                    <span className="text-[10px] tracking-[0.18em] uppercase bg-[#451C25] text-white px-2 py-0.5">
                      Featured
                    </span>
                  )}
                  <PersonaPill persona={s.persona} />
                  {!s.published && (
                    <span className="text-[10px] tracking-[0.18em] uppercase bg-[#6B4954]/15 text-[#6B4954] px-2 py-0.5">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#6B4954] mb-1">{s.description}</p>
                <p className="text-xs text-[#6B4954]">
                  <strong className="text-[#451C25]">{s.price}</strong>
                  {s.price_unit ? ` · ${s.price_unit}` : ""} ·{" "}
                  {s.features?.length || 0} features ·{" "}
                  <span className="font-mono">{s.cta_url}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`service-edit-${s.slug}`}
                  onClick={() => setEditing(s.id)}
                  className="p-2 hover:bg-[#451C25]/5"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  data-testid={`service-delete-${s.slug}`}
                  onClick={() => onDelete(s.id)}
                  className="p-2 hover:bg-[#451C25]/10 text-[#451C25]"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ServiceEditor
          axios={axios}
          item={editing === "new" ? null : items.find((s) => s.id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            fetchAll();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ServiceEditor({ axios, item, onClose, onSaved }) {
  const [d, setD] = useState(
    item || {
      title: "",
      slug: "",
      description: "",
      price: "",
      price_unit: "one-time",
      features: [],
      cta_label: "Book now",
      cta_url: "#booking",
      featured: false,
      persona: "both",
      order: 0,
      published: true,
    }
  );
  const [featuresText, setFeaturesText] = useState((item?.features || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = {
      ...d,
      features: featuresText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean),
      order: Number(d.order || 0),
    };
    if (!payload.title || !payload.description || !payload.price) {
      toast.error("Title, description, and price are required");
      return;
    }
    setSaving(true);
    try {
      if (item) {
        await axios.put(`/admin/services/${item.id}`, payload);
        toast.success("Service updated");
      } else {
        await axios.post("/admin/services", payload);
        toast.success("Service created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto py-12 px-4">
      <div className="absolute inset-0 bg-[#451C25]/70" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F5EFE7] border border-[#451C25]/10 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-[#451C25]">
            {item ? "Edit service" : "New service"}
          </h3>
          <button onClick={onClose} className="p-2">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Title *">
            <Input
              data-testid="service-title"
              value={d.title}
              onChange={(e) => upd("title", e.target.value)}
            />
          </Field>
          <Field label="Slug (auto if empty)">
            <Input
              data-testid="service-slug"
              value={d.slug}
              onChange={(e) => upd("slug", e.target.value)}
            />
          </Field>
          <Field label="Price * (e.g. $1,490 or €60)">
            <Input
              data-testid="service-price"
              value={d.price}
              onChange={(e) => upd("price", e.target.value)}
            />
          </Field>
          <Field label="Price unit">
            <Input
              data-testid="service-price-unit"
              placeholder="one-time / monthly / yearly"
              value={d.price_unit || ""}
              onChange={(e) => upd("price_unit", e.target.value)}
            />
          </Field>
          <Field label="Persona">
            <Select
              testid="service-persona"
              value={d.persona}
              onChange={(v) => upd("persona", v)}
              options={PERSONAS}
            />
          </Field>
          <Field label="Order (lower = first)">
            <Input
              type="number"
              data-testid="service-order"
              value={d.order}
              onChange={(e) => upd("order", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Description *">
            <Textarea
              data-testid="service-description"
              rows={3}
              value={d.description}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Features (one per line)">
            <Textarea
              data-testid="service-features"
              rows={6}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <Field label="CTA label">
            <Input
              data-testid="service-cta-label"
              value={d.cta_label}
              onChange={(e) => upd("cta_label", e.target.value)}
            />
          </Field>
          <Field label="CTA URL (#booking or external)">
            <Input
              data-testid="service-cta-url"
              value={d.cta_url}
              onChange={(e) => upd("cta_url", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap gap-5 items-center">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="service-featured"
              checked={d.featured}
              onChange={(e) => upd("featured", e.target.checked)}
            />
            Featured (show "Most chosen" badge)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="service-published"
              checked={d.published}
              onChange={(e) => upd("published", e.target.checked)}
            />
            Published
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25]"
          >
            Cancel
          </button>
          <button
            data-testid="service-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-7 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] disabled:opacity-50 transition-colors"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   COURSES PANEL
============================================================ */
export function CoursesPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    axios
      .get("/admin/courses")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load courses"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, [axios]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try {
      await axios.delete(`/admin/courses/${id}`);
      toast.success("Course deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="courses-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-[#451C25]">
            Paid Courses
          </h2>
          <p className="text-sm text-[#6B4954] mt-1">
            Courses hosted externally (Kajabi, Gumroad, Teachable, Stripe Payment
            Link…). Free resources can upsell to these.
          </p>
        </div>
        <button
          data-testid="course-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New Course
        </button>
      </div>

      {loading ? (
        <p className="text-[#6B4954]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#6B4954]">No courses yet.</p>
      ) : (
        <div className="border border-[#451C25]/10 bg-white divide-y divide-[#451C25]/10">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`course-row-${c.slug}`}
              className="p-5 flex items-center gap-5"
            >
              {c.cover_url && (
                <img
                  src={c.cover_url}
                  alt=""
                  className="w-20 h-20 object-cover hidden md:block"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-serif text-lg text-[#451C25]">{c.title}</h3>
                  <PersonaPill persona={c.persona} />
                  {!c.published && (
                    <span className="text-[10px] tracking-[0.18em] uppercase bg-[#6B4954]/15 text-[#6B4954] px-2 py-0.5">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#6B4954] mb-1">{c.description}</p>
                <p className="text-xs text-[#6B4954]">
                  <strong className="text-[#451C25]">{c.price}</strong>
                  {c.price_unit ? ` · ${c.price_unit}` : ""} ·{" "}
                  <a
                    href={c.cta_url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[#451C25] hover:underline inline-flex items-center gap-1"
                  >
                    {c.cta_url.length > 50 ? c.cta_url.slice(0, 50) + "…" : c.cta_url}
                    <ExternalLink size={10} />
                  </a>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`course-edit-${c.slug}`}
                  onClick={() => setEditing(c.id)}
                  className="p-2 hover:bg-[#451C25]/5"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  data-testid={`course-delete-${c.slug}`}
                  onClick={() => onDelete(c.id)}
                  className="p-2 hover:bg-[#451C25]/10 text-[#451C25]"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CourseEditor
          axios={axios}
          item={editing === "new" ? null : items.find((s) => s.id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            fetchAll();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function CourseEditor({ axios, item, onClose, onSaved }) {
  const [d, setD] = useState(
    item || {
      title: "",
      slug: "",
      description: "",
      price: "",
      price_unit: "one-time",
      cover_url: "",
      cta_label: "Get the course",
      cta_url: "",
      highlights: [],
      persona: "both",
      order: 0,
      published: true,
    }
  );
  const [highlightsText, setHighlightsText] = useState(
    (item?.highlights || []).join("\n")
  );
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = {
      ...d,
      highlights: highlightsText.split("\n").map((l) => l.trim()).filter(Boolean),
      order: Number(d.order || 0),
    };
    if (!payload.title || !payload.description || !payload.price || !payload.cta_url) {
      toast.error("Title, description, price, and CTA URL are required");
      return;
    }
    setSaving(true);
    try {
      if (item) {
        await axios.put(`/admin/courses/${item.id}`, payload);
        toast.success("Course updated");
      } else {
        await axios.post("/admin/courses", payload);
        toast.success("Course created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto py-12 px-4">
      <div className="absolute inset-0 bg-[#451C25]/70" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F5EFE7] border border-[#451C25]/10 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-[#451C25]">
            {item ? "Edit course" : "New course"}
          </h3>
          <button onClick={onClose} className="p-2">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Title *">
            <Input
              data-testid="course-title"
              value={d.title}
              onChange={(e) => upd("title", e.target.value)}
            />
          </Field>
          <Field label="Slug">
            <Input
              data-testid="course-slug"
              value={d.slug}
              onChange={(e) => upd("slug", e.target.value)}
            />
          </Field>
          <Field label="Price *">
            <Input
              data-testid="course-price"
              value={d.price}
              onChange={(e) => upd("price", e.target.value)}
            />
          </Field>
          <Field label="Price unit">
            <Input
              data-testid="course-price-unit"
              value={d.price_unit || ""}
              onChange={(e) => upd("price_unit", e.target.value)}
            />
          </Field>
          <Field label="Persona">
            <Select
              testid="course-persona"
              value={d.persona}
              onChange={(v) => upd("persona", v)}
              options={PERSONAS}
            />
          </Field>
          <Field label="Order">
            <Input
              type="number"
              data-testid="course-order"
              value={d.order}
              onChange={(e) => upd("order", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Description *">
            <Textarea
              data-testid="course-description"
              rows={4}
              value={d.description}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <Field label="Cover image URL">
            <Input
              data-testid="course-cover"
              value={d.cover_url || ""}
              onChange={(e) => upd("cover_url", e.target.value)}
            />
          </Field>
          <Field label="CTA label">
            <Input
              data-testid="course-cta-label"
              value={d.cta_label}
              onChange={(e) => upd("cta_label", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="CTA URL * (Kajabi, Gumroad, Stripe…)">
            <Input
              data-testid="course-cta-url"
              value={d.cta_url}
              onChange={(e) => upd("cta_url", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Highlights (one per line)">
            <Textarea
              data-testid="course-highlights"
              rows={5}
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap gap-5 items-center">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="course-published"
              checked={d.published}
              onChange={(e) => upd("published", e.target.checked)}
            />
            Published
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
            Cancel
          </button>
          <button
            data-testid="course-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-7 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] disabled:opacity-50 transition-colors"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FREE RESOURCES PANEL
============================================================ */
export function ResourcesPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get("/admin/free-resources"),
      axios.get("/admin/courses"),
    ])
      .then(([r1, r2]) => {
        setItems(r1.data || []);
        setCourses(r2.data || []);
      })
      .catch(() => toast.error("Couldn't load resources"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, [axios]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await axios.delete(`/admin/free-resources/${id}`);
      toast.success("Resource deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="resources-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-[#451C25]">
            Free Resources <span className="text-[#6B4954] text-base font-sans">(Atlas)</span>
          </h2>
          <p className="text-sm text-[#6B4954] mt-1">
            Lead magnets shown at <code>/atlas</code>. Each one captures an email
            and can upsell to one paid course.
          </p>
        </div>
        <button
          data-testid="resource-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New Resource
        </button>
      </div>

      {loading ? (
        <p className="text-[#6B4954]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#6B4954]">No resources yet.</p>
      ) : (
        <div className="border border-[#451C25]/10 bg-white divide-y divide-[#451C25]/10">
          {items.map((r) => {
            const upsell = courses.find((c) => c.slug === r.course_slug);
            return (
              <div
                key={r.id}
                data-testid={`resource-row-${r.slug}`}
                className="p-5 flex items-center gap-5"
              >
                {r.cover_url && (
                  <img
                    src={r.cover_url}
                    alt=""
                    className="w-20 h-20 object-cover hidden md:block"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-serif text-lg text-[#451C25]">{r.title}</h3>
                    <PersonaPill persona={r.persona} />
                    {!r.published && (
                      <span className="text-[10px] tracking-[0.18em] uppercase bg-[#6B4954]/15 text-[#6B4954] px-2 py-0.5">
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#6B4954] mb-1">{r.description}</p>
                  <p className="text-xs text-[#6B4954]">
                    {r.download_count || 0} downloads ·{" "}
                    {upsell ? (
                      <>
                        Upsells →{" "}
                        <span className="text-[#451C25] font-medium">
                          {upsell.title}
                        </span>
                      </>
                    ) : (
                      <span className="italic">no upsell</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`resource-edit-${r.slug}`}
                    onClick={() => setEditing(r.id)}
                    className="p-2 hover:bg-[#451C25]/5"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    data-testid={`resource-delete-${r.slug}`}
                    onClick={() => onDelete(r.id)}
                    className="p-2 hover:bg-[#451C25]/10 text-[#451C25]"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <ResourceEditor
          axios={axios}
          item={editing === "new" ? null : items.find((s) => s.id === editing)}
          courses={courses}
          onClose={() => setEditing(null)}
          onSaved={() => {
            fetchAll();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ResourceEditor({ axios, item, courses, onClose, onSaved }) {
  const [d, setD] = useState(
    item || {
      title: "",
      slug: "",
      description: "",
      cover_url: "",
      file_url: "",
      course_slug: "",
      persona: "both",
      order: 0,
      published: true,
    }
  );
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = {
      ...d,
      order: Number(d.order || 0),
      course_slug: d.course_slug || null,
    };
    if (!payload.title || !payload.description || !payload.file_url) {
      toast.error("Title, description, and file URL are required");
      return;
    }
    setSaving(true);
    try {
      if (item) {
        await axios.put(`/admin/free-resources/${item.id}`, payload);
        toast.success("Resource updated");
      } else {
        await axios.post("/admin/free-resources", payload);
        toast.success("Resource created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto py-12 px-4">
      <div className="absolute inset-0 bg-[#451C25]/70" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F5EFE7] border border-[#451C25]/10 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-[#451C25]">
            {item ? "Edit resource" : "New free resource"}
          </h3>
          <button onClick={onClose} className="p-2">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Title *">
            <Input
              data-testid="resource-title"
              value={d.title}
              onChange={(e) => upd("title", e.target.value)}
            />
          </Field>
          <Field label="Slug">
            <Input
              data-testid="resource-slug"
              value={d.slug}
              onChange={(e) => upd("slug", e.target.value)}
            />
          </Field>
          <Field label="Persona">
            <Select
              testid="resource-persona"
              value={d.persona}
              onChange={(v) => upd("persona", v)}
              options={PERSONAS}
            />
          </Field>
          <Field label="Order">
            <Input
              type="number"
              data-testid="resource-order"
              value={d.order}
              onChange={(e) => upd("order", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Description *">
            <Textarea
              data-testid="resource-description"
              rows={3}
              value={d.description}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Cover image URL">
            <Input
              data-testid="resource-cover"
              value={d.cover_url || ""}
              onChange={(e) => upd("cover_url", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="File URL * (Google Drive / Dropbox / direct PDF)">
            <Input
              data-testid="resource-file-url"
              value={d.file_url}
              onChange={(e) => upd("file_url", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Upsell to course (optional)">
            <Select
              testid="resource-course"
              value={d.course_slug || ""}
              onChange={(v) => upd("course_slug", v)}
              options={[
                { id: "", label: "— No upsell —" },
                ...courses.map((c) => ({ id: c.slug, label: c.title })),
              ]}
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center gap-5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="resource-published"
              checked={d.published}
              onChange={(e) => upd("published", e.target.checked)}
            />
            Published
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
            Cancel
          </button>
          <button
            data-testid="resource-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-7 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] disabled:opacity-50 transition-colors"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
